<template>
    <div class="flex flex-col md:flex-row h-screen overflow-hidden bg-gray-100">
        <!-- Sidebar -->
        <aside class="w-full md:w-64 bg-white shadow-lg overflow-y-auto">
            <div class="p-4 border-b border-gray-200">
                <h1 class="text-xl font-bold text-gray-800">Resort</h1>
                <p class="text-sm text-gray-500">Mapa Interativo</p>
            </div>
            <div class="p-2 relative">
                <input v-model="search" type="text" placeholder="Buscar atração..." class="w-full p-2 pl-8 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <i class="fas fa-search absolute left-4 top-5 text-gray-400"></i>
            </div>
            <nav class="mt-2">
                <ul>
                    <li v-for="(location, key) in filteredLocations" :key="key" @click="openModal(key)" class="p-3 border-b border-gray-100 cursor-pointer flex items-center hover:bg-blue-50 transition-colors">
                        <i :class="['fas', location.icon, location.color, 'mr-3']"></i>
                        <span>{{ location.title }}</span>
                    </li>
                </ul>
            </nav>
        </aside>

        <!-- Main Content -->
        <main class="flex-1 relative">
            <div ref="mapContainer" class="relative w-full h-full overflow-hidden touch-none" @mousedown="startDrag" @mouseup="stopDrag" @mouseleave="stopDrag" @mousemove="drag" @touchstart.prevent="startDrag" @touchend="stopDrag" @touchmove.prevent="drag" @wheel.prevent="handleWheel">
                <img ref="mapImage" :src="mapSrc" class="absolute top-0 left-0 select-none" :style="mapStyle" draggable="false" />

                <div class="zoom-controls absolute bottom-4 right-4 z-10 flex flex-col gap-2">
                    <button class="zoom-btn" @click="zoomIn"><i class="fas fa-plus"></i></button>
                    <button class="zoom-btn" @click="zoomOut"><i class="fas fa-minus"></i></button>
                </div>
            </div>
        </main>

        <!-- Modal -->
        <div v-if="modalVisible" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div class="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
                <div class="p-4 border-b border-gray-200 flex justify-between items-center">
                    <h3 class="text-xl font-semibold text-gray-800">{{ currentLocation.title }}</h3>
                    <button class="text-gray-500 hover:text-gray-700" @click="closeModal">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <div class="p-4">
                    <img :src="currentLocation.image" alt="Imagem" class="w-full h-48 object-cover rounded-lg mb-4" />
                    <div class="space-y-3">
                        <div>
                            <h4 class="font-medium text-gray-700">Descrição</h4>
                            <p class="text-gray-600">{{ currentLocation.description }}</p>
                        </div>
                        <div>
                            <h4 class="font-medium text-gray-700">Horário de Funcionamento</h4>
                            <p class="text-gray-600">{{ currentLocation.hours }}</p>
                        </div>
                        <div>
                            <h4 class="font-medium text-gray-700">Contato</h4>
                            <p class="text-gray-600">{{ currentLocation.contact }}</p>
                        </div>
                        <div>
                            <h4 class="font-medium text-gray-700">Serviços</h4>
                            <ul class="list-disc list-inside text-gray-600">
                                <li v-for="(service, index) in currentLocation.services" :key="index">{{ service }}</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const mapSrc = 'https://canabravaresort.com.br/wp-content/themes/canabrava/assets/img/mapa-canabrava.webp';
const scale = ref(1);
const posX = ref(0);
const posY = ref(0);
const dragging = ref(false);
const dragStartX = ref(0);
const dragStartY = ref(0);
const modalVisible = ref(false);
const currentLocationKey = ref(null);
const search = ref('');

const locations = {
    hotel: {
        title: "Hotel Principal",
        icon: "fa-hotel",
        color: "text-blue-500",
        image: "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=800&q=80",
        description: "O Hotel Principal oferece acomodações luxuosas com vista para o lago. Possui quartos amplos, serviço de quarto 24h e recepção bilíngue.",
        hours: "24 horas",
        contact: "recepcao@resortcanabrava.com.br | (71) 99999-9999",
        services: ["Wi-Fi gratuito", "Ar condicionado", "TV a cabo", "Frigobar"]
    },
    restaurante: {
        title: "Restaurante Canabrava",
        icon: "fa-utensils",
        color: "text-red-500",
        image: "https://images.unsplash.com/photo-1517248135467-4bab7a1e67b2?auto=format&fit=crop&w=800&q=80",
        description: "Restaurante com culinária regional e internacional, buffet variado e opções à la carte. Ambiente climatizado com vista panorâmica.",
        hours: "Café da manhã: 6h-10h | Almoço: 12h-15h | Jantar: 19h-23h",
        contact: "restaurante@resortcanabrava.com.br | (71) 88888-8888",
        services: ["Buffet variado", "Opções vegetarianas", "Cardápio infantil", "Bebidas artesanais"]
    },
    adega: {
        title: "Adega do Resort",
        icon: "fa-wine-glass-alt",
        color: "text-purple-500",
        image: "https://images.unsplash.com/photo-1556911220-e15f29a6b831?auto=format&fit=crop&w=800&q=80",
        description: "Adega climatizada com mais de 200 rótulos de vinhos nacionais e importados. Degustações guiadas disponíveis.",
        hours: "Terça a Domingo: 16h-23h",
        contact: "adega@resortcanabrava.com.br | (71) 77777-7777",
        services: ["Degustações guiadas", "Vinhos por taça", "Harmonizações", "Enólogos disponíveis"]
    },
    sorveteria: {
        title: "Sorveteria Artesanal",
        icon: "fa-ice-cream",
        color: "text-pink-500",
        image: "https://images.unsplash.com/photo-1497034825429-c343d7c6a68f?auto=format&fit=crop&w=800&q=80",
        description: "Sorvetes artesanais produzidos no local com frutas da estação e ingredientes premium. Mais de 30 sabores disponíveis.",
        hours: "Diariamente: 10h-22h",
        contact: "sorveteria@resortcanabrava.com.br | (71) 66666-6666",
        services: ["Sorvetes veganos", "Casquinhas artesanais", "Toppings variados", "Milkshakes"]
    },
    pastelaria: {
        title: "Pastelaria Mineira",
        icon: "fa-bread-slice",
        color: "text-amber-500",
        image: "https://images.unsplash.com/photo-1601050690597-df0568f70950?auto=format&fit=crop&w=800&q=80",
        description: "Pastéis artesanais feitos na hora com receitas tradicionais mineiras. Massa crocante e recheios variados.",
        hours: "Diariamente: 8h-20h",
        contact: "pastelaria@resortcanabrava.com.br | (71) 55555-5555",
        services: ["Pastéis doces e salgados", "Sucos naturais", "Café especial", "Opções sem glúten"]
    },
    piscina: {
        title: "Piscina Principal",
        icon: "fa-swimming-pool",
        color: "text-cyan-500",
        image: "https://images.unsplash.com/photo-1575429198097-0413ec5f8b89?auto=format&fit=crop&w=800&q=80",
        description: "Piscina de borda infinita com vista para o lago. Área de 500m² com profundidade variada e serviço de bar.",
        hours: "Diariamente: 8h-20h",
        contact: "lazer@resortcanabrava.com.br | (71) 44444-4444",
        services: ["Bar molhado", "Cadeiras de sol", "Toalhas disponíveis", "Acesso para cadeirantes"]
    },
    spa: {
        title: "Spa & Bem-estar",
        icon: "fa-spa",
        color: "text-green-500",
        image: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?auto=format&fit=crop&w=800&q=80",
        description: "Centro de bem-estar com massagens terapêuticas, tratamentos faciais e corporais, sauna e hidromassagem.",
        hours: "Terça a Domingo: 9h-21h (agendamento necessário)",
        contact: "spa@resortcanabrava.com.br | (71) 33333-3333",
        services: ["Massagens variadas", "Tratamentos faciais", "Sauna seca e úmida", "Pacotes especiais"]
    },
    academia: {
        title: "Academia Premium",
        icon: "fa-dumbbell",
        color: "text-gray-600",
        image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=800&q=80",
        description: "Academia equipada com aparelhos de última geração, sala de spinning e área para treinamento funcional.",
        hours: "Diariamente: 6h-22h",
        contact: "academia@resortcanabrava.com.br | (71) 22222-2222",
        services: ["Personal trainer", "Aulas em grupo", "Área de alongamento", "Toalhas e água disponíveis"]
    },
    quadra: {
        title: "Quadra Esportiva",
        icon: "fa-basketball-ball",
        color: "text-orange-500",
        image: "https://images.unsplash.com/photo-1547347298-4074fc3086f0?auto=format&fit=crop&w=800&q=80",
        description: "Quadra poliesportiva coberta para futebol, vôlei e basquete. Equipamentos disponíveis para empréstimo.",
        hours: "Diariamente: 7h-19h",
        contact: "esportes@resortcanabrava.com.br | (71) 11111-1111",
        services: ["Bolas disponíveis", "Arbitragem opcional", "Iluminação noturna", "Marcador eletrônico"]
    },
    playground: {
        title: "Playground Infantil",
        icon: "fa-child",
        color: "text-yellow-500",
        image: "https://images.unsplash.com/photo-1588072432836-e10032774350?auto=format&fit=crop&w=800&q=80",
        description: "Área segura e monitorada com brinquedos educativos para crianças de 2 a 12 anos. Monitores especializados.",
        hours: "Diariamente: 9h-18h",
        contact: "kids@resortcanabrava.com.br | (71) 00000-0000",
        services: ["Brinquedos educativos", "Atividades monitoradas", "Área coberta", "Fraldário"]
    },
    estacionamento: {
        title: "Estacionamento",
        icon: "fa-parking",
        color: "text-gray-700",
        image: "https://images.unsplash.com/photo-1605000797499-95a51c5269ae?auto=format&fit=crop&w=800&q=80",
        description: "Amplo estacionamento coberto e descoberto com mais de 300 vagas. Segurança 24 horas e carregadores para veículos elétricos.",
        hours: "24 horas",
        contact: "portaria@resortcanabrava.com.br | (71) 12345-6789",
        services: ["Vagas cobertas", "Segurança 24h", "Carregadores elétricos", "Serviço de valet"]
    }
};

const mapStyle = computed(() => ({
    transform: `translate(${posX.value}px, ${posY.value}px) scale(${scale.value})`,
    transition: dragging.value ? 'none' : 'transform 0.1s ease-out',
    width: '100%',
    height: '100%'
}));

const startDrag = (e) => {
    dragging.value = true;
    if (e.type.startsWith('touch')) {
        dragStartX.value = e.touches[0].clientX - posX.value;
        dragStartY.value = e.touches[0].clientY - posY.value;
    } else {
        dragStartX.value = e.clientX - posX.value;
        dragStartY.value = e.clientY - posY.value;
    }
};

const drag = (e) => {
    if (!dragging.value) return;
    if (e.type.startsWith('touch')) {
        posX.value = e.touches[0].clientX - dragStartX.value;
        posY.value = e.touches[0].clientY - dragStartY.value;
    } else {
        posX.value = e.clientX - dragStartX.value;
        posY.value = e.clientY - dragStartY.value;
    }
};

const stopDrag = () => {
    dragging.value = false;
};

const handleWheel = (e) => {
    zoomAtPoint(e, e.deltaY < 0 ? 0.1 : -0.1);
};

const zoomIn = (e) => zoomAtPoint(e, 0.2);
const zoomOut = (e) => zoomAtPoint(e, -0.2);

const zoomAtPoint = (e, delta) => {
    const container = mapContainer.value;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const cx = (e.clientX - rect.left - posX.value) / scale.value;
    const cy = (e.clientY - rect.top - posY.value) / scale.value;
    const newScale = Math.min(3, Math.max(0.5, scale.value + delta));
    posX.value -= cx * (newScale - scale.value);
    posY.value -= cy * (newScale - scale.value);
    scale.value = newScale;
};

const currentLocation = computed(() => locations[currentLocationKey.value] || {});
const filteredLocations = computed(() => {
    if (!search.value) return locations;
    return Object.fromEntries(
        Object.entries(locations).filter(([, loc]) =>
            loc.title.toLowerCase().includes(search.value.toLowerCase())
        )
    );
});

const mapImage = ref(null);
const mapContainer = ref(null);

const openModal = (key) => {
    currentLocationKey.value = key;
    modalVisible.value = true;
};

const closeModal = () => {
    modalVisible.value = false;
};
</script>

<style>
.zoom-btn {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 18px;
    color: #333;
    transition: all 0.2s;
}

.zoom-btn:hover {
    background-color: #f0f0f0;
    transform: scale(1.05);
}
</style>
